import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Division, DivisionService } from '../..';
import { Observable, of, tap } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Logger, ConfirmDialogComponent } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';

const log = new Logger('DivisionComponent');
@Component({
  selector: 'app-division',
  templateUrl: './division.component.html',
  styleUrls: ['./division.component.scss']
})

export class DivisionComponent<TDivision extends Division> implements OnInit {
  division$: Observable<TDivision> = of({} as TDivision);
  formGroup = new FormGroup({
    ClientKey: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(200)]),
    Id: new FormControl<string>({ value: '', disabled: true }, { nonNullable: true }),
    DivisionName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
  });
  divisionId: string = '0';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private divisionService: DivisionService<TDivision>,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog) { }

  ngOnInit() {
    log.debug('init');
    // get the division id from the parent route
    this.divisionId = this.route.snapshot.params['divisionId'];

    // if we are editing a record, get the record data
    if (this.divisionId && this.divisionId !== '0') {
      this.division$ = this.divisionService.getDivision(this.divisionId).pipe(
        tap((division) => {
          this.formGroup.patchValue(division);
          this.formGroup.markAllAsTouched();
        })
      );
    }
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    })
  }
  
  save() {
    this.trimControlValues(this.formGroup);
    if (this.formGroup.valid) {
      this.divisionService.saveDivison(this.formGroup.getRawValue() as TDivision).subscribe({
        next: (response) => {
          this.matSnackBar.open(
            `${this.formGroup.controls.DivisionName.value} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG
          );
          this.divisionService.reload();
          this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error saving event', error);
            this.matSnackBar.open('500 Error saving event', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          
          if (error.status === 400) {
            const apiValidations: any = error.error;
            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                if (this.formGroup?.get(validation.PropertyName)) {
                  const control = this.formGroup?.get(validation.PropertyName);
                  if (control) {
                    control.markAsTouched();
                    control.setErrors({ invalid: validation.ErrorMessage });
                    this.matSnackBar.open(validation.ErrorMessage, 'OK', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
                  }
                } else {
                  ///TODO: if we have cross field validation then show the validation error at the top of the screen
                  // if we have cross field validation then show the validation error at the top of the screen
                  // push general error messages to array this is displayed in a toast or dialog
                }
              });
            } else {
              this.matSnackBar.open(apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }


          }
        }
      });
    }
  }

  cancel(): void {
    this.formGroup.reset();
    this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  deleteRecord() {
    const record = this.formGroup.getRawValue();
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you wish to delete ${record.DivisionName}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.divisionService.deleteDivison(record.Id).subscribe({
            next: () => {
              this.matSnackBar.open(`${record.DivisionName} deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.divisionService.reload();
              this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
            },
            error: (error) => {
              log.error('Error deleting event', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            }
          });
        }
      });
  }

}
